/**
 * `/m/speakers/:speakerId/recordings` — read-only 回看.
 *
 * Same recording card as the student list, plus the expert section: merged
 * scores, per-character marks, each expert's note, and arbitration state.
 */
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { DimensionMerge, MarkType, MergeResult } from "@xinghai/shared";
import { EmptyState, Icon } from "@xinghai/ui";
import { speakersApi } from "../../lib/api";
import type { ExpertReview } from "../../lib/api";
import { qk } from "../../lib/queries";
import { RecordingListRow } from "../student/RecordingsPage";
import { dimensionLabel, MARK_LABEL } from "../annotator/labels";
import { ErrorNotice, LoadingBlock, PageHeader, Section } from "./common";
import { ANNOTATOR_TRACK_LABEL } from "./labels";

function scoreText(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(Math.round(value * 100));
}

function dimLine(key: string, dim: DimensionMerge | undefined): string {
  return `${dimensionLabel(key)} ${scoreText(dim?.value)}`;
}

function ExpertSection({ expert, text }: { expert: ExpertReview; text?: string | null }) {
  if (expert.hidden_reason === "pending_own_review") {
    return (
      <section className="xz-stack">
        <h2 className="gz-type-ui">专家标注</h2>
        <p className="gz-notice">
          <Icon id="ui-info" size={20} />
          你在这条录音上还有未完成的标注或仲裁，其他专家的判断先不显示。
        </p>
      </section>
    );
  }

  const merge = expert.merge?.result;
  const judgments = expert.judgments ?? [];
  const glyphs = [...(text ?? "")];
  let arbitration = "还没有专家标注";
  if (expert.arbitration && expert.arbitration.length > 0) {
    arbitration = `已仲裁 ${expert.arbitration.length} 项`;
  } else if (expert.merge?.needs_arbitration) {
    arbitration = "待仲裁";
  } else if (expert.merge) {
    arbitration = "不需要仲裁";
  }

  return (
    <section className="xz-stack">
      <h2 className="gz-type-ui">专家标注</h2>
      {merge ? <MergedScores merge={merge} glyphs={glyphs} /> : <p className="gz-muted">还没有汇总。</p>}
      {judgments.length > 0 && (
        <ul className="xz-stack">
          {judgments.map((j) => (
            <li key={`${j.display_name}-${j.submitted_at}`}>
              <strong>{j.display_name}</strong>
              {j.track && (
                <span className="gz-caption">
                  {" "}
                  · {ANNOTATOR_TRACK_LABEL[j.track] ?? j.track}
                </span>
              )}
              <p className="gz-caption">{j.note?.trim() ? j.note : "没有备注"}</p>
            </li>
          ))}
        </ul>
      )}
      <p className="gz-caption">仲裁：{arbitration}</p>
    </section>
  );
}

function MergedScores({ merge, glyphs }: { merge: MergeResult; glyphs: string[] }) {
  const utterance = Object.entries(merge.utterance ?? {});
  return (
    <div className="xz-stack">
      {utterance.length > 0 && (
        <ul className="xz-ai__dims">
          {utterance.map(([key, dim]) => (
            <li key={key}>
              <span>{dimensionLabel(key)}</span>
              <span>{scoreText(dim?.value)}</span>
            </li>
          ))}
          <li>
            <span>{dimensionLabel("listening_effort")}</span>
            <span>{scoreText(merge.listening_effort?.value)}</span>
          </li>
          <li>
            <span>{dimensionLabel("accentedness")}</span>
            <span>{scoreText(merge.accentedness?.value)}</span>
          </li>
        </ul>
      )}
      {merge.chars.length > 0 && (
        <ul className="xz-adm__tags">
          {merge.chars.map((ch) => {
            const mark = ch.mark_type?.value as MarkType | null;
            const glyph = glyphs[ch.char_index] ?? String(ch.char_index + 1);
            return (
              <li key={ch.char_index}>
                {glyph} {mark ? MARK_LABEL[mark] : "未标"}
                {ch.dims
                  ? `（${Object.entries(ch.dims)
                      .map(([key, dim]) => dimLine(key, dim))
                      .join("，")}）`
                  : ""}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function StudentRecordingsPage() {
  const { speakerId = "" } = useParams();
  const q = useQuery({
    queryKey: qk.speakerRecordings(speakerId),
    queryFn: ({ signal }) => speakersApi.recordings(speakerId, signal),
    enabled: speakerId.length > 0,
  });

  const name = q.data?.speaker.display_name ?? "学生";

  return (
    <div className="xz-adm">
      <PageHeader
        title={`回看 ${q.data ? name : ""}`.trim()}
        description="只读。可以听录音、看 AI 评分和专家标注，不能修改。"
        actions={
          <Link className="gz-button gz-button--secondary" to="/m/speakers">
            返回学生名单
          </Link>
        }
      />
      {q.isPending ? (
        <LoadingBlock rows={3} label="正在加载录音" />
      ) : q.isError ? (
        <ErrorNotice error={q.error} onRetry={() => void q.refetch()} />
      ) : q.data.recordings.length === 0 ? (
        <EmptyState title="还没有录音" description={`${name} 还没有交过录音。`} />
      ) : (
        <Section title="录音" subtitle={`${q.data.recordings.length} 条`}>
          <div className="xz-stack">
            {q.data.recordings.map((r) => (
              <RecordingListRow key={r.id} r={r}>
                <ExpertSection expert={r.expert} text={r.material_text} />
              </RecordingListRow>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
